import { Row, Col, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DateInput } from '../../../ui/DateInput/DateInput';
import { IFamilyMemberDto, ISystemCodeByTypeDto } from '../../../../utils/constants/interfaces';
import { useSecondaryAdultMember } from './useSecondaryAdultMember';
import styles from './SecondaryAdultMemberSection.module.css';

type FamilyMemberKey = keyof IFamilyMemberDto;

interface SecondaryAdultMemberSectionProps {
  secondaryMember: IFamilyMemberDto | null;
  fieldErrors: string[];
  titleOptions: { value: number; label: string }[];
  suffixOptions: { value: number; label: string }[];
  memberStatusOptions: ISystemCodeByTypeDto[];
  loadingMemberStatuses: boolean;
  savingSecondaryMember: boolean;
  onUpdateSecondaryMember: <K extends FamilyMemberKey>(key: K, value: IFamilyMemberDto[K]) => void;
  onCopyAddressFromPrimary: () => void;
  onSaveSecondaryMember: () => void;
  onCancelSecondaryMember: () => void;
  onDeleteSecondaryMember?: () => void;
}

export const SecondaryAdultMemberSection = ({
  secondaryMember,
  fieldErrors,
  titleOptions,
  suffixOptions,
  memberStatusOptions,
  loadingMemberStatuses,
  savingSecondaryMember,
  onUpdateSecondaryMember,
  onCopyAddressFromPrimary,
  onSaveSecondaryMember,
  onCancelSecondaryMember,
  onDeleteSecondaryMember
}: SecondaryAdultMemberSectionProps) => {
  const manager = useSecondaryAdultMember({
    secondaryMember,
    fieldErrors
  });

  return (
    <div className={styles.card}>
      <h4 className={styles.sectionTitle}>Secondary Adult Family Member</h4>
      <p className={styles.formInstructions}>
        Add information for an additional adult family member.
      </p>

      {/* Personal Information */}
      <div className={styles.subsectionCard}>
        <h5 className={styles.subsectionTitle}>Personal Information</h5>
        <Row className="g-2 mb-2">
          <Col md={1}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>ID</Form.Label>
              <div className={styles.idValue}>{secondaryMember?.legacyMemberId || 'N/A'}</div>
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Title</Form.Label>
              <Form.Select
                size="sm"
                value={
                  secondaryMember?.title !== null && secondaryMember?.title !== undefined
                    ? secondaryMember.title
                    : ''
                }
                onChange={(e) =>
                  onUpdateSecondaryMember(
                    'title',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }>
                <option value="">- Select -</option>
                {titleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label
                className={
                  fieldErrors.includes('family:firstName')
                    ? `${styles.required} is-invalid`
                    : styles.required
                }>
                First Name
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.firstName || ''}
                onChange={(e) => onUpdateSecondaryMember('firstName', e.target.value)}
                isInvalid={fieldErrors.includes('family:firstName')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Middle</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.middleName || ''}
                onChange={(e) => onUpdateSecondaryMember('middleName', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label
                className={
                  fieldErrors.includes('family:lastName')
                    ? `${styles.required} is-invalid`
                    : styles.required
                }>
                Last Name
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.lastName || ''}
                onChange={(e) => onUpdateSecondaryMember('lastName', e.target.value)}
                isInvalid={fieldErrors.includes('family:lastName')}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Suffix</Form.Label>
              <Form.Select
                size="sm"
                value={
                  secondaryMember?.suffix !== null && secondaryMember?.suffix !== undefined
                    ? secondaryMember.suffix
                    : ''
                }
                onChange={(e) =>
                  onUpdateSecondaryMember(
                    'suffix',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }>
                <option value="">-</option>
                {suffixOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="g-2 mb-2">
          <Col md={7}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                value={secondaryMember?.emailAddress || ''}
                onChange={(e) => onUpdateSecondaryMember('emailAddress', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label
                className={
                  fieldErrors.includes('family:memberStatusId')
                    ? `${styles.required} is-invalid`
                    : styles.required
                }>
                Status
              </Form.Label>
              <Form.Select
                size="sm"
                value={secondaryMember?.memberStatusId || ''}
                onChange={(e) =>
                  onUpdateSecondaryMember(
                    'memberStatusId',
                    e.target.value === '' ? 0 : Number(e.target.value)
                  )
                }
                isInvalid={fieldErrors.includes('family:memberStatusId')}>
                <option value="">- Select -</option>
                {memberStatusOptions.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.description || status.codeValue}
                  </option>
                ))}
              </Form.Select>
              {loadingMemberStatuses && <small className="text-muted">Loading...</small>}
            </Form.Group>
          </Col>
          <Col md={2}>
            <DateInput
              label="Birth Date"
              value={secondaryMember?.dateOfBirthUtc || null}
              onChange={(date) => onUpdateSecondaryMember('dateOfBirthUtc', date)}
              isInvalid={fieldErrors.includes('family:dateOfBirthUtc')}
              required={false}
            />
          </Col>
        </Row>
      </div>

      {/* Relationship & Contact */}
      <div className={styles.subsectionCard}>
        <h5 className={styles.subsectionTitle}>Relationship & Contact</h5>
        <Row className="g-2 mb-2">
          <Col md={4}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Relationship</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.relationship || ''}
                onChange={(e) => onUpdateSecondaryMember('relationship', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Home Phone</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.homePhone || ''}
                onChange={(e) => onUpdateSecondaryMember('homePhone', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Cell Phone</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.cellPhone || ''}
                onChange={(e) => onUpdateSecondaryMember('cellPhone', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Address */}
      <div className={styles.subsectionCard}>
        <h5 className={styles.subsectionTitle}>Address</h5>
        <Row className="g-2 mb-2">
          <Col md={6}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Address</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.address || ''}
                onChange={(e) => onUpdateSecondaryMember('address', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.address2 || ''}
                onChange={(e) => onUpdateSecondaryMember('address2', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="g-2 mb-2">
          <Col md={6}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>City</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.city || ''}
                onChange={(e) => onUpdateSecondaryMember('city', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>State</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.state || ''}
                onChange={(e) => onUpdateSecondaryMember('state', e.target.value)}
                className={styles.stateInput}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.zipCode || ''}
                onChange={(e) => onUpdateSecondaryMember('zipCode', e.target.value)}
                className={styles.zipInput}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="g-2 mb-3">
          <Col>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onCopyAddressFromPrimary}
              className="mt-1">
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              Copy Address from Primary
            </Button>
          </Col>
        </Row>
      </div>

      {/* Additional Details */}
      <div className={styles.subsectionCard}>
        <h5 className={styles.subsectionTitle}>Additional Details</h5>
        <Row className="g-2 mb-2">
          <Col md={4}>
            <DateInput
              label="First Joined Date"
              value={secondaryMember?.firstJoinedDateUtc ?? null}
              onChange={(date) => onUpdateSecondaryMember('firstJoinedDateUtc', date)}
              isInvalid={fieldErrors.includes('family:firstJoinedDateUtc')}
              required
            />
          </Col>
          <Col md={4}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>Profession</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.profession || ''}
                onChange={(e) => onUpdateSecondaryMember('profession', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="g-2 mb-2">
          <Col md={2}>
            <Form.Group
              className={`${styles.compactFormGroup} d-flex align-items-center justify-content-center h-100`}>
              <Form.Check
                type="checkbox"
                id="secondaryNraMember"
                label="NRA Member"
                checked={secondaryMember?.isNraMember || false}
                onChange={(e) => onUpdateSecondaryMember('isNraMember', e.target.checked)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className={styles.compactFormGroup}>
              <Form.Label>NRA Number</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={secondaryMember?.nraNumber || ''}
                onChange={(e) => onUpdateSecondaryMember('nraNumber', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Action Buttons */}
      <div className={styles.actionButtons}>
        <Button variant="primary" onClick={onSaveSecondaryMember} disabled={savingSecondaryMember}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Save Family Member
          {savingSecondaryMember && <span className="spinner-border spinner-border-sm ms-2" />}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onCancelSecondaryMember}
          disabled={savingSecondaryMember}>
          Cancel
        </Button>
        {secondaryMember?.id && onDeleteSecondaryMember && (
          <Button
            variant="outline-danger"
            onClick={onDeleteSecondaryMember}
            disabled={savingSecondaryMember}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete
            {savingSecondaryMember && <span className="spinner-border spinner-border-sm ms-2" />}
          </Button>
        )}
      </div>
    </div>
  );
};
